// Single source of wording for the "Consent to Treatment & Release of
// Information (On-Site School Services)" form. The public fill page, the
// therapist's view/print modal and the letterhead PDF all render from this so
// the three never drift. Sections 1–6 are the paper form verbatim; the school
// name is substituted wherever the paper form names the school. The online
// form takes one signature (section 6) covering sections 4–6.

export const SCHOOL_CONSENT_TITLE =
  'Consent to Treatment & Release of Information (On-Site School Services)';

export const DEFAULT_SCHOOL_NAME = 'Sacred Heart Model School';

export const PRACTICE_NAME = 'Avenues Unlimited Counseling Center';

// Header field labels, in the paper form's order.
export const HEADER_LABELS = {
  clientName: 'Client Name (Child)',
  clientDob: 'Date of Birth',
  schoolName: 'School Name',
  gradeTeacher: 'Grade/Teacher',
};

// Section 5 "Authorized Information to be Shared" checkboxes (data keys → label).
export const SHARE_OPTIONS = [
  { key: 'shareAttendance', label: 'Treatment Attendance' },
  { key: 'shareProgressGoals', label: 'Treatment Progress / Goals' },
  { key: 'shareClassroomStrategies', label: 'Recommendations for Classroom Strategies' },
];
export const SHARE_OTHER_LABEL = 'Other (specify)';

// Turns a section-2 bullet ({ lead, text }) or a plain-string bullet into one line.
export function bulletLine(b) {
  return typeof b === 'string' ? b : `${b.lead}: ${b.text}`;
}

// Returns the six sections with the school name substituted in.
export function withSchool(schoolName) {
  const school = (schoolName || '').trim() || DEFAULT_SCHOOL_NAME;
  return {
    purpose: {
      title: '1. Purpose of This Form',
      body:
        `Avenues Unlimited Counseling Center ("the Practice") is partnering with ${school} to provide on-site mental health treatment for students who may benefit from professional support. This Consent to Treatment & Release of Information form outlines the nature of services, confidentiality protections, and the specific permissions granted or withheld by the parent/legal guardian.`,
    },
    services: {
      title: '2. Description of Services',
      bullets: [
        { lead: 'Assessment & Evaluation', text: "Gathering information about the child's emotional, behavioral, and social functioning." },
        { lead: 'Therapeutic Sessions', text: "Individual (and, if applicable, group or family) therapy sessions aimed at addressing the child's mental health and behavioral needs." },
        { lead: 'Coordination of Care', text: "Communicating with school personnel (e.g., teachers, counselors) to support the child's treatment and academic success, only with explicit written permission (see below)." },
      ],
    },
    confidentiality: {
      title: '3. Confidentiality & Limitations',
      intro:
        "Your child's privacy is protected by federal (HIPAA) and state (KRS Chapter 335) laws. Information shared during therapy will generally remain confidential. However, confidentiality is not absolute. By law, the treating therapist must disclose information if:",
      bullets: [
        'There is reason to suspect child abuse or neglect (KRS 620.030).',
        'There is an imminent risk of harm to the child or someone else.',
        'A court order or subpoena legally compels release of information.',
      ],
    },
    consent: {
      title: '4. Consent to Treatment',
      intro:
        'I, the undersigned, certify that I am the parent or legal guardian of the above-named child. I hereby give my voluntary consent to any qualified staff at Avenues Unlimited Counseling Center to provide mental health services to my child at the school. I understand that:',
      bullets: [
        'Treatment is voluntary and can be terminated by myself or the child at any time.',
        "I will be informed about the child's progress and any recommended changes in treatment.",
        'The therapist may discuss basic scheduling and attendance information with school staff to facilitate services.',
        'If additional communication with school personnel about the child\'s treatment is needed, I understand I must grant separate written authorization (below).',
      ],
    },
    release: {
      title: '5. Release of Information to School Personnel (Optional but Recommended)',
      checkOne: '(Check one)',
      yesLead: 'Yes',
      yesText:
        `I authorize the staff of Avenues Unlimited Counseling Center to discuss and/or share relevant treatment information (e.g., treatment goals, progress updates, recommended accommodations) with the following individuals at ${school} for the purpose of coordinating care and supporting my child's academic success:`,
      personnelLabel: 'Authorized School Personnel',
      sharedHeading: 'Authorized Information to be Shared:',
      shareOptions: SHARE_OPTIONS,
      otherLabel: SHARE_OTHER_LABEL,
      expirationLead: 'This authorization will expire on:',
      expirationHint: '(date or event)',
      expirationTail: 'I understand I may revoke it at any time in writing.',
      noLead: 'No',
      noText:
        'I do not authorize release of any specific treatment information to school personnel beyond the minimum needed for scheduling and attendance.',
    },
    acknowledgment: {
      title: '6. Acknowledgment and Acceptance',
      body:
        `I have read (or had read to me) and understand all of the above sections. By signing below, I agree to the terms outlined and consent to mental health services for my child at ${school}. I acknowledge that I have had the opportunity to ask questions and have them answered to my satisfaction.`,
      signatureLabel: 'Parent/Guardian Signature',
      printedNameLabel: 'Printed Name',
      dateLabel: 'Date',
    },
  };
}
